<template>
    <div
        class="modal fade"
        id="design-location-mismatch-modal"
        tabindex="-1"
        aria-hidden="true"
        data-keyboard="true"
        data-backdrop="static"
    >
        <div class="modal-dialog modal-dialog-centered mm-dialog">
            <div class="modal-content mm-content">
                <!-- Close -->
                <button
                    type="button"
                    class="mm-close"
                    data-dismiss="modal"
                    @click="$emit('close')"
                    aria-label="Close"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1l12 12M13 1L1 13" stroke="#888" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>

                <!-- Warning icon -->
                <div class="mm-icon-wrap">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                        <circle cx="22" cy="22" r="22" fill="#FFF3CD"/>
                        <path d="M22 13v12M22 28v2" stroke="#E6A817" stroke-width="2.5" stroke-linecap="round"/>
                    </svg>
                </div>

                <!-- Title -->
                <h5 class="mm-title">Design Can't Be Applied</h5>

                <!-- Subtitle -->
                <p class="mm-subtitle">
                    <span class="mm-design-name">{{ designName }}</span>
                    has artwork on print location(s) this product doesn't support.
                </p>

                <!-- Location comparison cards -->
                <div class="mm-cards">
                    <div class="mm-card mm-card--design">
                        <div class="mm-card-label">Design Uses</div>
                        <div class="mm-card-tags">
                            <span
                                v-for="flip in requiredFlips"
                                :key="flip"
                                class="mm-tag"
                                :class="missingSet.has(flip.toLowerCase()) ? 'mm-tag--blocked' : 'mm-tag--ok'"
                            >
                                <svg v-if="missingSet.has(flip.toLowerCase())" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                                <svg v-else width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                {{ flip }}
                            </span>
                        </div>
                    </div>

                    <div class="mm-card mm-card--product">
                        <div class="mm-card-label">Product Supports</div>
                        <div class="mm-card-tags">
                            <span
                                v-for="flip in availableFlips"
                                :key="flip"
                                class="mm-tag mm-tag--ok"
                            >
                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                {{ flip }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Hint -->
                <p class="mm-hint">
                    Please select a design that only uses this product's available print areas.
                </p>

                <!-- OK button -->
                <button
                    type="button"
                    class="mm-btn-ok"
                    data-dismiss="modal"
                    @click="$emit('close')"
                >
                    Got it
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "DesignLocationMismatchModal",
    props: {
        designName: {
            type: String,
            default: "This design"
        },
        requiredFlips: {
            type: Array,
            default: () => []
        },
        availableFlips: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        missingFlips() {
            const available = new Set(
                (this.availableFlips || []).map((f) => String(f).toLowerCase())
            );
            return (this.requiredFlips || []).filter(
                (f) => !available.has(String(f).toLowerCase())
            );
        },
        missingSet() {
            return new Set(this.missingFlips.map((f) => String(f).toLowerCase()));
        }
    }
};
</script>

<style scoped>
.mm-dialog {
    max-width: 460px;
    width: 100%;
}

.mm-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14);
    padding: 36px 32px 28px;
    text-align: center;
    position: relative;
}

.mm-close {
    position: absolute;
    top: 14px;
    right: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    opacity: 0.6;
    transition: opacity 0.15s;
}
.mm-close:hover {
    opacity: 1;
}

.mm-icon-wrap {
    margin-bottom: 16px;
}

.mm-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 10px;
}

.mm-subtitle {
    font-size: 14px;
    color: #555;
    margin-bottom: 22px;
    line-height: 1.6;
}

.mm-design-name {
    font-weight: 600;
    color: #1a1a1a;
}

.mm-cards {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    text-align: left;
}

.mm-card {
    flex: 1;
    border-radius: 10px;
    padding: 12px 14px;
}

.mm-card--design {
    background: #fafafa;
    border: 1px solid #e8e8e8;
}

.mm-card--product {
    background: #f0faf4;
    border: 1px solid #c6e8d2;
}

.mm-card-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    margin-bottom: 8px;
}

.mm-card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.mm-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
}

.mm-tag--ok {
    background: #e6f4ec;
    color: #2a7a4b;
}

.mm-tag--blocked {
    background: #fde8e8;
    color: #c0392b;
}

.mm-hint {
    font-size: 12px;
    color: #888;
    margin-bottom: 22px;
    line-height: 1.5;
}

.mm-btn-ok {
    display: inline-block;
    background: #e6730a;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 40px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
}
.mm-btn-ok:hover {
    background: #c95f05;
}
</style>
